import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import ChatSessionItem from './ChatSessionItem';
import { useDarkMode } from '../contexts/DarkModeContext';
import { Link } from 'lucide-react';
import config from '../config.js';
import Tooltip from '../common_jsx/Tooltip';

const Sidebar = ({
  isExpanded,
  onToggle,
  onNewChat,
  sessions,
  activeSessionId,
  onSessionClick,
  onSessionDelete,
  onSessionPin,
  onSessionRename,
  isLoadingSessions,
  setSelectedScreen,
  onOpenIntegrations,
  streamingSessions,
  unreadSessions,
  hasMoreSessions,
  isLoadingMoreSessions,
  loadMoreSessions,
}) => {
  const { isDarkMode } = useDarkMode();
  const isProduction = ['prod', 'production'].includes(config.env?.toLowerCase());

  const sentinelRef = useRef(null);
  const loadMoreRef = useRef(loadMoreSessions);
  useEffect(() => { loadMoreRef.current = loadMoreSessions; }, [loadMoreSessions]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMoreSessions) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && typeof loadMoreRef.current === 'function') loadMoreRef.current(); },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMoreSessions]);

  const categorizeSessionsByTime = (sessions) => {
    const now = Date.now();
    const oneDayMs = 24 * 60 * 60 * 1000;
    const oneWeekMs = 7 * oneDayMs;

    const todayCutoff = new Date();
    todayCutoff.setHours(0, 0, 0, 0);
    const todayTimestamp = todayCutoff.getTime();
    const weekCutoff = now - oneWeekMs;

    return {
      today: sessions.filter((s) => s.update_time >= todayTimestamp),
      thisWeek: sessions.filter((s) => s.update_time >= weekCutoff && s.update_time < todayTimestamp),
      older: sessions.filter((s) => s.update_time < weekCutoff),
    };
  };

  const pinnedSessionsList = sessions
    .filter((s) => s.is_pinned)
    .sort((a, b) => b.update_time - a.update_time);
  const categorized = categorizeSessionsByTime(sessions.filter((s) => !s.is_pinned));

  // Collapsed sidebar state
  if (!isExpanded) {
    return (
      <div 
        className={`fixed left-0 top-0 flex flex-col items-center pt-3 px-2 gap-3 h-screen transition-all duration-300 z-[2100] ${
          isDarkMode 
            ? 'bg-[#0f172a] border-r border-[#1e293b]' 
            : 'bg-white border-r border-gray-200'
        }`} 
        style={{ width: '56px' }}
      >
        {/* Expand button with subtle background */}
        <Tooltip text="Expand sidebar" isDarkMode={isDarkMode} direction="right">
          <button
            onClick={onToggle}
            className={`flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200 ${
              isDarkMode 
                ? 'bg-[#1e293b]/50 hover:bg-[#1e293b] text-gray-400 hover:text-gray-200 shadow-sm shadow-black/10' 
                : 'bg-gray-100/80 hover:bg-gray-200 text-gray-500 hover:text-gray-700 shadow-sm shadow-gray-200/50'
            }`}
          >
            {/* Sidebar toggle icon - rectangle with line */}
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <line x1="9" y1="3" x2="9" y2="21" />
            </svg>
          </button>
        </Tooltip>
        
        {/* New Chat button with subtle background */}
        <Tooltip text="New chat" isDarkMode={isDarkMode} direction="right">
          <button
            onClick={onNewChat}
            className={`flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200 ${
              isDarkMode
                ? 'bg-[#1e293b]/50 hover:bg-[#1e293b] text-gray-400 hover:text-gray-200 shadow-sm shadow-black/10'
                : 'bg-gray-100/80 hover:bg-gray-200 text-gray-500 hover:text-gray-700 shadow-sm shadow-gray-200/50'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </Tooltip>

        {/* Spacer to push integrations to bottom */}
        <div className="flex-1" />

        {/* Integrations button - collapsed */}
        {!isProduction && (
          <Tooltip text="Integrations" isDarkMode={isDarkMode} direction="right">
            <button
              onClick={onOpenIntegrations}
              className={`flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200 mb-3 ${
                isDarkMode
                  ? 'bg-[#1e293b]/50 hover:bg-[#1e293b] text-gray-400 hover:text-gray-200 shadow-sm shadow-black/10'
                  : 'bg-gray-100/80 hover:bg-gray-200 text-gray-500 hover:text-gray-700 shadow-sm shadow-gray-200/50'
              }`}
            >
              <Link className="w-5 h-5" />
            </button>
          </Tooltip>
        )}
      </div>
    );
  }

  // Expanded sidebar state
  return (
    <div 
      className={`fixed left-0 top-0 flex flex-col h-screen transition-all duration-300 z-[2100] ${
        isDarkMode 
          ? 'bg-[#0f172a] border-r border-[#1e293b]' 
          : 'bg-white border-r border-gray-200'
      }`} 
      style={{ width: '260px' }}
    >
      {/* Header with logo and collapse button - height matches navbar */}
      <div className={`flex items-center justify-between px-3 h-14`}>
        <div className="flex items-center gap-2">
          {config.brandLogoUrl && (
            <img
              src={config.brandLogoUrl}
              alt={config.brandName}
              className="select-none"
              style={{
                height: `${32 * config.brandLogoScale}px`,
                width: 'auto',
                maxWidth: '140px',
                objectFit: 'contain',
                filter: isDarkMode ? 'invert(1) brightness(1.2)' : 'none',
                transition: 'filter 0.2s'
              }}
            />
          )}
          {config.brandShortName && (
            <span className={`text-base font-semibold ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {config.brandShortName}
            </span>
          )}
        </div>
        {/* Collapse button with subtle background */}
        <Tooltip text="Collapse sidebar" isDarkMode={isDarkMode} direction="right">
          <button
            onClick={onToggle}
            className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 ${
              isDarkMode 
                ? 'bg-[#1e293b]/30 hover:bg-[#1e293b] text-gray-400 hover:text-gray-200' 
                : 'bg-gray-100/50 hover:bg-gray-200 text-gray-500 hover:text-gray-700'
            }`}
          >
            {/* Sidebar toggle icon - rectangle with line */}
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <line x1="9" y1="3" x2="9" y2="21" />
            </svg>
          </button>
        </Tooltip>
      </div>

      {/* New Chat Button - soft subtle border */}
      <div className="px-3 py-3">
        <button
          onClick={onNewChat}
          className={`flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 ${
            isDarkMode
              ? 'border border-[#334155]/60 hover:border-[#475569]/80 hover:bg-[#1e293b]/40 text-gray-300 hover:text-gray-100'
              : 'border border-gray-300/60 hover:border-gray-400/80 hover:bg-gray-100/60 text-gray-600 hover:text-gray-800'
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
          </svg>
          <span>New chat</span>
        </button>
      </div>

      {/* Chat History */}
      <div className="flex-1 overflow-y-auto px-2 sidebar-scrollbar">
        {isLoadingSessions ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563eb]"></div>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Pinned sessions */}
            {pinnedSessionsList.length > 0 && (
              <div>
                <h3 className="px-2 py-1 text-base font-semibold text-gray-600 dark:text-gray-500">
                  Pinned
                </h3>
                <div className="mt-1 space-y-0.5">
                  {pinnedSessionsList.map((session) => (
                    <ChatSessionItem
                      key={session.id}
                      id={session.id}
                      summary={session.summary}
                      isActive={activeSessionId === session.id}
                      isDarkMode={isDarkMode}
                      onClick={() => {
                        onSessionClick(session.id);
                        setSelectedScreen('ChatScreen');
                      }}
                      onDelete={onSessionDelete}
                      onPin={onSessionPin}
                      onRename={onSessionRename}
                      isPinned={true}

                      isStreaming={streamingSessions?.has(session.id) ?? false}
                      isUnread={unreadSessions?.has(session.id) ?? false}
                    />
                  ))}
                </div>
                {/* Divider below pinned section */}
                <div className={`mt-3 border-t ${isDarkMode ? 'border-[#1e293b]' : 'border-gray-200'}`} />
              </div>
            )}

            {categorized.today.length > 0 && (
              <div>
                <h3 className="px-2 py-1 text-base font-semibold text-gray-600 dark:text-gray-500">
                  Today
                </h3>
                <div className="mt-1 space-y-0.5">
                  {categorized.today
                    .sort((a, b) => b.update_time - a.update_time)
                    .map((session) => (
                      <ChatSessionItem
                        key={session.id}
                        id={session.id}
                        summary={session.summary}
                        isActive={activeSessionId === session.id}
                        isDarkMode={isDarkMode}
                        onClick={() => {
                          onSessionClick(session.id);
                          setSelectedScreen('ChatScreen');
                        }}
                        onDelete={onSessionDelete}
                        onPin={onSessionPin}
                        onRename={onSessionRename}
                        isPinned={session.is_pinned ?? false}
  
                        isStreaming={streamingSessions?.has(session.id) ?? false}
                        isUnread={unreadSessions?.has(session.id) ?? false}
                      />
                    ))}
                </div>
              </div>
            )}

            {categorized.thisWeek.length > 0 && (
              <div>
                <h3 className="px-2 py-1 text-base font-semibold text-gray-600 dark:text-gray-500">
                  This week
                </h3>
                <div className="mt-1 space-y-0.5">
                  {categorized.thisWeek
                    .sort((a, b) => b.update_time - a.update_time)
                    .map((session) => (
                      <ChatSessionItem
                        key={session.id}
                        id={session.id}
                        summary={session.summary}
                        isActive={activeSessionId === session.id}
                        isDarkMode={isDarkMode}
                        onClick={() => {
                          onSessionClick(session.id);
                          setSelectedScreen('ChatScreen');
                        }}
                        onDelete={onSessionDelete}
                        onPin={onSessionPin}
                        onRename={onSessionRename}
                        isPinned={session.is_pinned ?? false}
  
                        isStreaming={streamingSessions?.has(session.id) ?? false}
                        isUnread={unreadSessions?.has(session.id) ?? false}
                      />
                    ))}
                </div>
              </div>
            )}

            {categorized.older.length > 0 && (
              <div>
                <h3 className="px-2 py-1 text-base font-semibold text-gray-600 dark:text-gray-500">
                  Older
                </h3>
                <div className="mt-1 space-y-0.5">
                  {categorized.older
                    .sort((a, b) => b.update_time - a.update_time)
                    .map((session) => (
                      <ChatSessionItem
                        key={session.id}
                        id={session.id}
                        summary={session.summary}
                        isActive={activeSessionId === session.id}
                        isDarkMode={isDarkMode}
                        onClick={() => {
                          onSessionClick(session.id);
                          setSelectedScreen('ChatScreen');
                        }}
                        onDelete={onSessionDelete}
                        onPin={onSessionPin}
                        onRename={onSessionRename}
                        isPinned={session.is_pinned ?? false}
  
                        isStreaming={streamingSessions?.has(session.id) ?? false}
                        isUnread={unreadSessions?.has(session.id) ?? false}
                      />
                    ))}
                </div>
              </div>
            )}

            {sessions.length === 0 && (
              <p className="px-3 py-8 text-center text-sm text-gray-500">
                No chat sessions yet
              </p>
            )}

            {hasMoreSessions && (
              <div ref={sentinelRef} className="py-4 flex justify-center">
                {isLoadingMoreSessions && (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#2563eb]" />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Integrations button - expanded (at bottom) */}
      {!isProduction && (
        <div className="px-3 py-3 border-t" style={{ borderColor: isDarkMode ? '#1e293b' : '#e5e7eb' }}>
          <button
            onClick={onOpenIntegrations}
            className={`flex items-center gap-3 w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
              isDarkMode
                ? 'text-gray-400 hover:text-gray-200 hover:bg-[#1e293b]'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            <Link className="w-5 h-5" />
            <span>Integrations</span>
          </button>
        </div>
      )}
    </div>
  );
};

Sidebar.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onNewChat: PropTypes.func.isRequired,
  sessions: PropTypes.array.isRequired,
  activeSessionId: PropTypes.string,
  onSessionClick: PropTypes.func.isRequired,
  onSessionDelete: PropTypes.func.isRequired,
  onSessionPin: PropTypes.func.isRequired,
  onSessionRename: PropTypes.func.isRequired,
  isLoadingSessions: PropTypes.bool.isRequired,
  setSelectedScreen: PropTypes.func.isRequired,
  onOpenIntegrations: PropTypes.func.isRequired,
  streamingSessions: PropTypes.instanceOf(Set),
  unreadSessions: PropTypes.instanceOf(Set),
  hasMoreSessions: PropTypes.bool,
  isLoadingMoreSessions: PropTypes.bool,
  loadMoreSessions: PropTypes.func,
};

export default Sidebar;
